import { useCallback, useEffect, useRef, useState } from 'react';
import type { AssistantMessage } from '@triedge/ai-assistant-ui';
import type {
  DomainRouterMutationArgs,
  DomainRouterMutationData,
  DomainRouterQueryArgs,
  DomainRouterQueryData,
} from '../../api/domainRouterClient';
import {
  buildAssistantFeedbackInput,
  isQueueableFeedbackError,
  type AdmissionsAssistantFeedbackInput,
} from './assistantFeedback';
import {
  drainAssistantFeedbackQueue,
  enqueueAssistantFeedback,
  getQueuedAssistantFeedbackCount,
} from './assistantFeedbackQueue';
import type {
  AdmissionsAIContextualAction,
  AdmissionsAIMessageFeedback,
  AdmissionsAssistantFeedbackSubmission,
  AdmissionsAssistantLessonSignal,
  AdmissionsAssistantMessageMetadata,
  AdmissionsCitation,
} from './types';

type AssistantFeedbackAck = {
  accepted: boolean;
  feedbackId: string;
  replayHash: string;
  artifactRefs: string[];
};

type AssistantFeedbackAuthority = {
  feedback: {
    feedbackId: string;
    feedbackArtifactRef: string;
    replayHash: string | null;
    artifactStatus: 'present' | 'missing' | 'mismatch';
    route: string | null;
    persona: string | null;
    projectionPlanHash: string | null;
    outcomeRef: string | null;
    submittedAt: string | null;
  };
  learning: {
    status: 'collecting' | 'ready';
    artifactBacked: boolean;
    statusSource: 'episode' | 'route' | 'global' | 'unavailable';
    updatedAt: string | null;
    artifactUpdatedAt: string | null;
    replayMatchRate: number | null;
  };
  runtimePolicy: {
    policyId: string;
    revision: number;
    executionMode: 'shadow' | 'live';
    policySource: 's3-object' | 'artifact-file' | 'env-json' | 'embedded-default';
    updatedAt: string | null;
  };
  lessonSignals: AdmissionsAssistantFeedbackSubmission['lessonSignals'];
  sourceRefs: AdmissionsAssistantFeedbackSubmission['authoritySourceRefs'];
};

type AssistantFeedbackAuthorityRequest = {
  feedbackId: string;
  feedbackArtifactRef: string;
  replayHash?: string | null;
};

type AssistantFeedbackAuthorityResult = {
  authority: AssistantFeedbackAuthority | null;
  errorMessage: string | null;
};

type QueryTrigger<Name extends string, Args, Data> = (
  operation: Name,
  args: Args,
  preferCacheValue?: boolean,
) => Promise<Data>;

type MutationTrigger<Args, Data> = (args: Args) => Promise<Data>;

type AssistantFeedbackControllerInput = {
  triggerSubmitAssistantFeedback: MutationTrigger<
    DomainRouterMutationArgs<'submitAssistantFeedback'>,
    DomainRouterMutationData<'submitAssistantFeedback'>
  >;
  triggerGetAssistantFeedbackAuthority: QueryTrigger<
    'getAssistantFeedbackAuthority',
    DomainRouterQueryArgs<'getAssistantFeedbackAuthority'>,
    DomainRouterQueryData<'getAssistantFeedbackAuthority'>
  >;
  onSetLastActionFeedback: (message: string | null) => void;
};

const FEEDBACK_EVENT_ARTIFACT_PREFIX = 'artifacts/hrm-ace/feedback-events/';

const toRecord = (value: unknown): Record<string, unknown> | null => (
  value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null
);

const toLessonFailureClass = (value: unknown): AdmissionsAssistantLessonSignal['failureClass'] => (
  value === 'execution-context'
  || value === 'policy-contract'
  || value === 'domain-readiness'
  || value === 'evidence-coherence'
  || value === 'security'
  || value === 'lineage'
    ? value
    : null
);

const toLessonLifecycleStage = (value: unknown): AdmissionsAssistantLessonSignal['lifecycleStage'] => (
  value === 'A' || value === 'B' || value === 'C'
    ? value
    : null
);

const toLessonSeverity = (value: unknown): AdmissionsAssistantLessonSignal['severity'] => (
  value === 'low' || value === 'medium' || value === 'high' || value === 'critical'
    ? value
    : null
);

const selectFeedbackArtifactRef = (artifactRefs: string[]): string | null => (
  artifactRefs.find((entry) => entry.startsWith(FEEDBACK_EVENT_ARTIFACT_PREFIX))
  ?? artifactRefs[0]
  ?? null
);

const toAssistantFeedbackAuthority = (payload: unknown): AssistantFeedbackAuthority | null => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }
  const candidate = payload as {
    feedback?: unknown;
    learning?: unknown;
    runtimePolicy?: unknown;
    lessonSignals?: unknown;
    sourceRefs?: unknown;
  };
  const feedback = toRecord(candidate.feedback);
  const learning = toRecord(candidate.learning);
  const runtimePolicy = toRecord(candidate.runtimePolicy);
  if (!feedback || !learning || !runtimePolicy) {
    return null;
  }

  const artifactStatus = feedback.artifactStatus;
  const learningStatus = learning.status;
  const learningStatusSource = learning.statusSource;
  const executionMode = runtimePolicy.executionMode;
  const policySource = runtimePolicy.policySource;
  if (
    typeof feedback.feedbackId !== 'string'
    || typeof feedback.feedbackArtifactRef !== 'string'
    || (feedback.replayHash != null && typeof feedback.replayHash !== 'string')
    || (artifactStatus !== 'present' && artifactStatus !== 'missing' && artifactStatus !== 'mismatch')
    || (feedback.route != null && typeof feedback.route !== 'string')
    || (feedback.persona != null && typeof feedback.persona !== 'string')
    || (feedback.projectionPlanHash != null && typeof feedback.projectionPlanHash !== 'string')
    || (feedback.outcomeRef != null && typeof feedback.outcomeRef !== 'string')
    || (feedback.submittedAt != null && typeof feedback.submittedAt !== 'string')
    || (learningStatus !== 'collecting' && learningStatus !== 'ready')
    || typeof learning.artifactBacked !== 'boolean'
    || (
      learningStatusSource !== 'episode'
      && learningStatusSource !== 'route'
      && learningStatusSource !== 'global'
      && learningStatusSource !== 'unavailable'
    )
    || (learning.updatedAt != null && typeof learning.updatedAt !== 'string')
    || (learning.artifactUpdatedAt != null && typeof learning.artifactUpdatedAt !== 'string')
    || (learning.replayMatchRate != null && typeof learning.replayMatchRate !== 'number')
    || typeof runtimePolicy.policyId !== 'string'
    || typeof runtimePolicy.revision !== 'number'
    || (executionMode !== 'shadow' && executionMode !== 'live')
    || (
      policySource !== 's3-object'
      && policySource !== 'artifact-file'
      && policySource !== 'env-json'
      && policySource !== 'embedded-default'
    )
    || (runtimePolicy.updatedAt != null && typeof runtimePolicy.updatedAt !== 'string')
  ) {
    return null;
  }

  const lessonSignals = Array.isArray(candidate.lessonSignals)
    ? candidate.lessonSignals.flatMap((entry) => {
      const node = toRecord(entry);
      if (!node || typeof node.lessonId !== 'string' || typeof node.verdict !== 'string' || typeof node.gateMode !== 'string' || typeof node.sourcePath !== 'string') {
        return [];
      }
      if (
        node.verdict !== 'PASS'
        && node.verdict !== 'FAIL'
        && node.verdict !== 'STALE'
        && node.verdict !== 'MISSING'
        && node.verdict !== 'ADVISORY_WARN'
      ) {
        return [];
      }
      if (node.gateMode !== 'advisory' && node.gateMode !== 'blocking') {
        return [];
      }
      return [{
        lessonId: node.lessonId,
        verdict: node.verdict,
        gateMode: node.gateMode,
        failureClass: toLessonFailureClass(node.failureClass),
        lifecycleStage: toLessonLifecycleStage(node.lifecycleStage),
        severity: toLessonSeverity(node.severity),
        sourcePath: node.sourcePath,
      }] satisfies NonNullable<AssistantFeedbackAuthority['lessonSignals']>;
    })
    : [];

  const sourceRefs = Array.isArray(candidate.sourceRefs)
    ? candidate.sourceRefs.flatMap((entry) => {
      const node = toRecord(entry);
      if (!node || typeof node.kind !== 'string' || typeof node.ref !== 'string' || typeof node.present !== 'boolean') {
        return [];
      }
      return [{
        kind: node.kind,
        ref: node.ref,
        present: node.present,
      }] satisfies NonNullable<AssistantFeedbackAuthority['sourceRefs']>;
    })
    : [];

  return {
    feedback: {
      feedbackId: feedback.feedbackId,
      feedbackArtifactRef: feedback.feedbackArtifactRef,
      replayHash: typeof feedback.replayHash === 'string' ? feedback.replayHash : null,
      artifactStatus,
      route: typeof feedback.route === 'string' ? feedback.route : null,
      persona: typeof feedback.persona === 'string' ? feedback.persona : null,
      projectionPlanHash: typeof feedback.projectionPlanHash === 'string' ? feedback.projectionPlanHash : null,
      outcomeRef: typeof feedback.outcomeRef === 'string' ? feedback.outcomeRef : null,
      submittedAt: typeof feedback.submittedAt === 'string' ? feedback.submittedAt : null,
    },
    learning: {
      status: learningStatus,
      artifactBacked: learning.artifactBacked,
      statusSource: learningStatusSource,
      updatedAt: typeof learning.updatedAt === 'string' ? learning.updatedAt : null,
      artifactUpdatedAt: typeof learning.artifactUpdatedAt === 'string' ? learning.artifactUpdatedAt : null,
      replayMatchRate: typeof learning.replayMatchRate === 'number' ? learning.replayMatchRate : null,
    },
    runtimePolicy: {
      policyId: runtimePolicy.policyId,
      revision: runtimePolicy.revision,
      executionMode,
      policySource,
      updatedAt: typeof runtimePolicy.updatedAt === 'string' ? runtimePolicy.updatedAt : null,
    },
    lessonSignals,
    sourceRefs,
  };
};

const toFeedbackSubmissionFromAuthority = (input: {
  feedback: AdmissionsAIMessageFeedback;
  ack: AssistantFeedbackAck;
  submittedAt: string;
  authority: AssistantFeedbackAuthority | null;
  authorityErrorMessage?: string | null;
}): AdmissionsAssistantFeedbackSubmission => ({
  feedback: input.feedback,
  submissionStatus: 'accepted',
  feedbackId: input.ack.feedbackId,
  replayHash: input.ack.replayHash,
  artifactRefs: input.ack.artifactRefs,
  feedbackArtifactRef: input.authority?.feedback.feedbackArtifactRef ?? selectFeedbackArtifactRef(input.ack.artifactRefs),
  feedbackArtifactStatus: input.authority?.feedback.artifactStatus ?? null,
  learningStatus: input.authority?.learning.status ?? null,
  learningUpdatedAt: input.authority?.learning.updatedAt ?? null,
  learningArtifactBacked: input.authority?.learning.artifactBacked ?? null,
  learningStatusSource: input.authority?.learning.statusSource ?? null,
  replayMatchRate: input.authority?.learning.replayMatchRate ?? null,
  runtimeExecutionMode: input.authority?.runtimePolicy.executionMode ?? null,
  runtimePolicySource: input.authority?.runtimePolicy.policySource ?? null,
  lessonSignals: input.authority?.lessonSignals ?? [],
  authoritySourceRefs: input.authority?.sourceRefs ?? [],
  authorityErrorMessage: input.authorityErrorMessage ?? null,
  authorityResolvedAt: input.authority ? new Date().toISOString() : null,
  errorMessage: null,
  submittedAt: input.authority?.feedback.submittedAt ?? input.submittedAt,
});

const buildFeedbackSuccessMessage = (submission: AdmissionsAssistantFeedbackSubmission): string => {
  if (submission.feedbackArtifactStatus === 'missing') {
    return `Feedback captured (${submission.feedbackId ?? 'unknown'}), but the authority artifact is missing.`;
  }
  if (submission.feedbackArtifactStatus === 'mismatch') {
    return `Feedback captured (${submission.feedbackId ?? 'unknown'}), but authority verification failed.`;
  }
  if (submission.learningArtifactBacked === false) {
    return `Feedback captured (${submission.feedbackId ?? 'unknown'}). Learning status unavailable.`;
  }
  if (submission.learningStatus === 'ready') {
    return `Feedback captured (${submission.feedbackId ?? 'unknown'}). Learning status ready.`;
  }
  if (submission.learningStatus === 'collecting') {
    return `Feedback captured (${submission.feedbackId ?? 'unknown'}). Learning status collecting.`;
  }
  return `Feedback captured (${submission.feedbackId ?? 'unknown'}). Authority details unavailable.`;
};

const toFeedbackMetadata = (
  message: AssistantMessage<AdmissionsCitation, AdmissionsAIContextualAction>,
): AdmissionsAssistantMessageMetadata | null => {
  const metadata = message.metadata;
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) {
    return null;
  }
  return metadata as AdmissionsAssistantMessageMetadata;
};

const toAssistantFeedbackAck = (payload: unknown): AssistantFeedbackAck | null => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }
  const candidate = payload as {
    accepted?: unknown;
    feedbackId?: unknown;
    replayHash?: unknown;
    artifactRefs?: unknown;
  };
  if (typeof candidate.accepted !== 'boolean' || typeof candidate.feedbackId !== 'string' || typeof candidate.replayHash !== 'string') {
    return null;
  }
  return {
    accepted: candidate.accepted,
    feedbackId: candidate.feedbackId,
    replayHash: candidate.replayHash,
    artifactRefs: Array.isArray(candidate.artifactRefs)
      ? candidate.artifactRefs.filter((entry): entry is string => typeof entry === 'string' && entry.length > 0)
      : [],
  };
};

export const useAssistantFeedbackController = (input: AssistantFeedbackControllerInput) => {
  const [feedbackSubmissionByMessageId, setFeedbackSubmissionByMessageId] = useState<Record<string, AdmissionsAssistantFeedbackSubmission>>({});
  const [feedbackToastMessage, setFeedbackToastMessage] = useState<string | null>(null);
  const positiveFeedbackTimeoutRef = useRef<number | null>(null);
  const feedbackMessagesRef = useRef<AssistantMessage<AdmissionsCitation, AdmissionsAIContextualAction>[]>([]);
  const feedbackSubmissionStateRef = useRef<Record<string, AdmissionsAssistantFeedbackSubmission>>({});

  const emitPositiveFeedbackToast = useCallback((message: string) => {
    if (positiveFeedbackTimeoutRef.current != null) {
      window.clearTimeout(positiveFeedbackTimeoutRef.current);
    }
    setFeedbackToastMessage(message);
    positiveFeedbackTimeoutRef.current = window.setTimeout(() => {
      setFeedbackToastMessage(null);
      positiveFeedbackTimeoutRef.current = null;
    }, 1800);
  }, []);

  const upsertFeedbackSubmission = useCallback((
    messageId: string,
    nextValue: Partial<AdmissionsAssistantFeedbackSubmission>,
  ) => {
    setFeedbackSubmissionByMessageId((current) => ({
      ...current,
      [messageId]: {
        ...(current[messageId] ?? { submissionStatus: 'submitting' }),
        ...nextValue,
      },
    }));
  }, []);

  useEffect(() => {
    feedbackSubmissionStateRef.current = feedbackSubmissionByMessageId;
  }, [feedbackSubmissionByMessageId]);

  useEffect(() => () => {
    if (positiveFeedbackTimeoutRef.current != null) {
      window.clearTimeout(positiveFeedbackTimeoutRef.current);
    }
  }, []);

  const submitAssistantFeedbackPayload = useCallback(async (
    payload: AdmissionsAssistantFeedbackInput,
  ): Promise<AssistantFeedbackAck> => {
    const response = await input.triggerSubmitAssistantFeedback({
      input: payload,
    });
    const ack = toAssistantFeedbackAck(response);
    if (!ack || !ack.accepted) {
      throw new Error('Assistant feedback was not accepted by the feedback spine.');
    }
    return ack;
  }, [input]);

  const loadAssistantFeedbackAuthority = useCallback(async (
    request: AssistantFeedbackAuthorityRequest,
  ): Promise<AssistantFeedbackAuthorityResult> => {
    try {
      const payload = await input.triggerGetAssistantFeedbackAuthority(
        'getAssistantFeedbackAuthority',
        request,
      );
      const authority = toAssistantFeedbackAuthority(payload);
      if (!authority) {
        return {
          authority: null,
          errorMessage: 'Authority details are unavailable for this feedback event.',
        };
      }
      return {
        authority,
        errorMessage: null,
      };
    } catch (error) {
      return {
        authority: null,
        errorMessage: error instanceof Error
          ? error.message
          : 'Authority details are unavailable for this feedback event.',
      };
    }
  }, [input]);

  const submitAssistantFeedbackForMessage = useCallback(async (request: {
    messageId: string;
    feedback: AdmissionsAIMessageFeedback;
    payload: AdmissionsAssistantFeedbackInput;
    allowQueue: boolean;
    emitSuccessToast: boolean;
  }) => {
    upsertFeedbackSubmission(request.messageId, {
      feedback: request.feedback,
      submissionStatus: 'submitting',
      errorMessage: null,
      submittedAt: request.payload.ts,
    });

    try {
      const ack = await submitAssistantFeedbackPayload(request.payload);
      if (request.emitSuccessToast) {
        emitPositiveFeedbackToast('Thanks for your feedback!');
      }
      const feedbackArtifactRef = selectFeedbackArtifactRef(ack.artifactRefs);
      const authorityResult = feedbackArtifactRef
        ? await loadAssistantFeedbackAuthority({
          feedbackId: ack.feedbackId,
          feedbackArtifactRef,
          replayHash: ack.replayHash,
        })
        : {
          authority: null,
          errorMessage: 'Authority details are unavailable for this feedback event.',
        };
      const submission = toFeedbackSubmissionFromAuthority({
        feedback: request.feedback,
        ack,
        submittedAt: request.payload.ts,
        authority: authorityResult.authority,
        authorityErrorMessage: authorityResult.errorMessage,
      });
      upsertFeedbackSubmission(request.messageId, submission);
      input.onSetLastActionFeedback(buildFeedbackSuccessMessage(submission));
    } catch (error) {
      if (request.allowQueue && isQueueableFeedbackError(error)) {
        await enqueueAssistantFeedback(request.payload);
        upsertFeedbackSubmission(request.messageId, {
          feedback: request.feedback,
          submissionStatus: 'queued',
          errorMessage: null,
          submittedAt: request.payload.ts,
        });
        input.onSetLastActionFeedback('Feedback queued locally; sync pending.');
        return;
      }

      const failureMessage = error instanceof Error
        ? error.message
        : 'Unable to submit assistant feedback right now.';
      upsertFeedbackSubmission(request.messageId, {
        feedback: undefined,
        submissionStatus: 'failed',
        errorMessage: failureMessage,
        submittedAt: request.payload.ts,
      });
      input.onSetLastActionFeedback(failureMessage);
    }
  }, [
    emitPositiveFeedbackToast,
    input,
    loadAssistantFeedbackAuthority,
    submitAssistantFeedbackPayload,
    upsertFeedbackSubmission,
  ]);

  const handleAssistantFeedbackRequest = useCallback((messageId: string, feedback: string) => {
    if (feedback !== 'up' && feedback !== 'down') {
      input.onSetLastActionFeedback('Unsupported assistant feedback value.');
      return;
    }

    const message = feedbackMessagesRef.current.find((entry) => entry.messageId === messageId);
    if (!message) {
      input.onSetLastActionFeedback('This assistant response is unavailable for feedback right now.');
      return;
    }

    const existingSubmission = feedbackSubmissionStateRef.current[messageId];
    if (existingSubmission?.submissionStatus === 'submitting') {
      return;
    }
    if (existingSubmission?.submissionStatus === 'accepted' && existingSubmission.feedback === feedback) {
      return;
    }

    const metadata = toFeedbackMetadata(message);
    const payloadResult = buildAssistantFeedbackInput({
      assistantMessageId: messageId,
      feedback,
      metadata,
      submittedAt: existingSubmission?.submittedAt ?? new Date().toISOString(),
    });
    if (!payloadResult.ok) {
      upsertFeedbackSubmission(messageId, {
        feedback: undefined,
        submissionStatus: 'failed',
        errorMessage: payloadResult.error,
      });
      input.onSetLastActionFeedback(payloadResult.error);
      return;
    }

    void submitAssistantFeedbackForMessage({
      messageId,
      feedback,
      payload: payloadResult.value,
      allowQueue: true,
      emitSuccessToast: true,
    });
  }, [input, submitAssistantFeedbackForMessage, upsertFeedbackSubmission]);

  const flushQueuedAssistantFeedback = useCallback(async () => {
    const queuedCount = await getQueuedAssistantFeedbackCount();
    if (queuedCount === 0) {
      return;
    }

    const result = await drainAssistantFeedbackQueue(async (payload) => {
      const ack = await submitAssistantFeedbackPayload(payload);
      const feedbackArtifactRef = selectFeedbackArtifactRef(ack.artifactRefs);
      const authorityResult = feedbackArtifactRef
        ? await loadAssistantFeedbackAuthority({
          feedbackId: ack.feedbackId,
          feedbackArtifactRef,
          replayHash: ack.replayHash,
        })
        : {
          authority: null,
          errorMessage: 'Authority details are unavailable for this feedback event.',
        };
      const submission = toFeedbackSubmissionFromAuthority({
        feedback: payload.feedback,
        ack,
        submittedAt: payload.ts,
        authority: authorityResult.authority,
        authorityErrorMessage: authorityResult.errorMessage,
      });
      upsertFeedbackSubmission(payload.assistantMessageId, submission);
    });

    if (result.submitted > 0) {
      input.onSetLastActionFeedback(
        result.failed > 0
          ? `Feedback sync completed: ${result.submitted} submitted, ${result.failed} pending.`
          : `Feedback sync completed: ${result.submitted} submitted.`
      );
    }
  }, [
    input,
    loadAssistantFeedbackAuthority,
    submitAssistantFeedbackPayload,
    upsertFeedbackSubmission,
  ]);

  useEffect(() => {
    void flushQueuedAssistantFeedback();

    const handleOnline = () => {
      void flushQueuedAssistantFeedback();
    };
    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, [flushQueuedAssistantFeedback]);

  const setFeedbackMessages = useCallback((
    messages: AssistantMessage<AdmissionsCitation, AdmissionsAIContextualAction>[],
  ) => {
    feedbackMessagesRef.current = messages;
  }, []);

  const resetFeedbackControllerState = useCallback(() => {
    setFeedbackSubmissionByMessageId({});
    setFeedbackToastMessage(null);
    if (positiveFeedbackTimeoutRef.current != null) {
      window.clearTimeout(positiveFeedbackTimeoutRef.current);
      positiveFeedbackTimeoutRef.current = null;
    }
  }, []);

  return {
    feedbackSubmissionByMessageId,
    feedbackToastMessage,
    handleAssistantFeedbackRequest,
    setFeedbackMessages,
    resetFeedbackControllerState,
  };
};
